import React, { useState, useMemo, useCallback } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { ChevronDown, ChevronRight, Settings, Globe } from 'lucide-react';
import { useComponentMetrics, getMetricLabel } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';

import { calculateVariation, makeRenderVariation } from '../utils/variationUtils';
/**
 * MatomoGeographyChart - Composant pour afficher la géographie des visiteurs
 * Structure: Continent > Pays > Région > Ville
 * Mêmes colonnes que le composant Canaux
 */


/**
 * ⚠️ **`conversions` et non `nb_visits_converted`.**
 *
 * Le second compte les **visites** ayant converti : une visite atteignant trois objectifs y
 * compte pour 1. La colonne ne disait donc pas la même chose que celle des régies, ni que la
 * carte au-dessus du tableau. Le service rend désormais, sur chaque ligne, le dénombrement de
 * l'objectif retenu sous `conversions` — le repli couvre une réponse servie par un cache
 * antérieur au déploiement.
 */
/*
 * ─── Les colonnes, décrites une fois ───────────────────────────────────────────────────────
 *
 * **Le tableau rendait ses colonnes dans un ordre figé**, recopié à chacun des quatre niveaux
 * — continent, pays, région, ville — et au pied. Choisir le modèle « E-commerce » réglait bien
 * *quelles* métriques afficher, mais pas leur ordre.
 *
 * ⚠️ **Trois familles de noms cohabitent ici**, et les confondre donnerait des variations
 * calculées contre `undefined`, c'est-à-dire −100 % partout :
 *
 * - les lignes arrivent **brutes de Matomo** (`nb_visits`, `nb_actions`, `sum_visit_length`) ;
 * - le pied de tableau porte des totaux **normalisés** par le composant (`visits`, `pageviews`,
 *   `sumVisitLength`) ;
 * - plusieurs colonnes sont **dérivées** et n'existent dans aucune des deux : rebond, durée
 *   moyenne et pages par visite se calculent.
 *
 * D'où trois accesseurs par colonne. `calcBounceRate` et `calcConversionRate` restent les
 * fonctions du composant : les recopier ici ferait deux définitions à tenir d'accord.
 */
const COLONNES = (calcBounceRate, calcConversionRate) => ({
  visites: {
    champ: 'nb_visits',
    valeur: (l) => l.nb_visits || 0,
    total: (t) => t.visits || 0,
    totalPrecedent: (p) => p.visits || 0,
    format: (v, f) => f(v)
  },
  visiteurs: {
    champ: 'nb_uniq_visitors',
    valeur: (l) => l.nb_uniq_visitors || 0,
    total: (t) => t.visitors || 0,
    totalPrecedent: (p) => p.visitors || 0,
    format: (v, f) => f(v)
  },
  rebond: {
    champ: 'bounce_rate',
    valeur: (l) => calcBounceRate(l),
    total: (t) => t.bounceRate || 0,
    format: (v, f) => `${f(v, 1)}%`,
    inverse: true
  },
  pages_vues: {
    champ: 'nb_actions',
    valeur: (l) => l.nb_actions || 0,
    total: (t) => t.pageviews || 0,
    totalPrecedent: (p) => p.pageviews || 0,
    format: (v, f) => f(v)
  },
  taux_conv: {
    champ: 'conversion_rate',
    valeur: (l) => calcConversionRate(l),
    total: (t) => (t.visits > 0 ? (t.conversions / t.visits * 100) : 0),
    totalPrecedent: (p) => p.conversionRate || 0,
    format: (v, f) => `${f(v, 2)}%`
  },
  conv: {
    champ: 'nb_visits_converted',
    // Matomo rend l'un ou l'autre selon le rapport : on prend celui qui est là.
    valeur: (l) => (l.conversions ?? l.nb_visits_converted) || 0,
    total: (t) => t.conversions || 0,
    totalPrecedent: (p) => p.conversions || 0,
    format: (v, f) => f(v)
  },
  valeur_conv: {
    champ: 'revenue',
    valeur: (l) => l.revenue || 0,
    total: (t) => t.revenue || 0,
    totalPrecedent: (p) => p.revenue || 0,
    format: (v, f) => `${f(v, 2)}\u20ac`
  },
  duree_moy: {
    champ: 'sum_visit_length',
    valeur: (l) => (l.nb_visits > 0 ? (l.sum_visit_length || 0) / l.nb_visits : 0),
    total: (t) => (t.visits > 0 ? (t.sumVisitLength || 0) / t.visits : 0),
    totalPrecedent: (p) => p.avgTime || 0,
    duree: true
  },
  pages_visite: {
    champ: 'nb_actions_per_visit',
    valeur: (l) => (l.nb_visits > 0 ? (l.nb_actions || 0) / l.nb_visits : 0),
    total: (t) => (t.visits > 0 ? (t.pageviews || 0) / t.visits : 0),
    totalPrecedent: (p) => p.pagesPerVisit || 0,
    format: (v, f) => f(v, 2)
  },
  transactions: {
    champ: 'transactions',
    valeur: (l) => l.transactions || 0,
    total: (t) => t.transactions || 0,
    format: (v, f) => f(v, 0)
  },
  chiffre_affaires: {
    champ: 'totalRevenue',
    valeur: (l) => l.totalRevenue || 0,
    total: (t) => t.totalRevenue || 0,
    format: (v, f) => `${f(v, 2)}\u20ac`
  },
  taux_transaction: {
    champ: 'transactionRate',
    valeur: (l) => l.transactionRate || 0,
    total: (t) => t.transactionRate || 0,
    format: (v, f) => `${f(v, 2)}%`
  },
  panier_moyen: {
    champ: 'avgOrderValue',
    valeur: (l) => (l.transactions > 0 ? (l.totalRevenue || 0) / l.transactions : 0),
    total: (t) => (t.transactions > 0 ? (t.totalRevenue || 0) / t.transactions : 0),
    format: (v, f) => `${f(v, 2)}\u20ac`
  }
});

/** La durée, en minutes:secondes. */
const formaterDuree = (secondes) => {
  const minutes = Math.floor((secondes || 0) / 60);
  const reste = Math.floor((secondes || 0) % 60);
  return `${minutes}:${reste.toString().padStart(2, '0')}`;
};

const MatomoGeographyChart = ({
  geography = [],
  previousGeography = [],
  /**
   * Le nombre de visiteurs du site sur la période — voir `MatomoChannelsChart`.
   *
   * La géographie sous-estimait de son côté : les six cents visites non localisées forment un
   * segment « Inconnu » dont les visiteurs uniques ne sont pas calculables, et leur absence
   * tirait le total vers le bas.
   */
  visiteursDuSite = null,
  compareEnabled = false,
  formatNumber,
  loading = false,
  accountId = 'global',
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'matomo_geography'
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [expandedContinents, setExpandedContinents] = useState({});
  const [expandedCountries, setExpandedCountries] = useState({});
  const [expandedRegions, setExpandedRegions] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);
  const ITEMS_PER_PAGE = 10;

  // Hook pour les métriques du composant (réactif aux changements)
  const componentMetrics = useComponentMetrics('matomo_geography', accountId);

  // Fonction helper pour vérifier si une métrique est sélectionnée
  const isMetricActive = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);

  /**
   * L'intitulé d'une colonne, pris dans la table commune aux trois solutions de mesure.
   *
   * Les intitulés étaient écrits en dur — « Visites », « Visiteurs », « Pages/Visite ». Renommer
   * la table des libellés n'avait donc aucun effet ici, et le tableau continuait d'annoncer
   * autre chose que le reste de l'application : c'est ainsi que « Utilisateurs » restait
   * introuvable alors que la colonne était bien là.
   */
  const libelleColonne = (cle) => getMetricLabel(cle, 'matomo_geography', accountId);



  /**
   * Ce que le camembert de droite représente.
   *
   * Il montrait les conversions quel que soit le tableau — y compris sur un compte e-commerce
   * où la colonne Conversions n'est pas affichée, et où ce sont les **transactions** qu'on
   * regarde. Le graphique et le tableau annonçaient alors deux choses différentes sans que rien
   * ne le dise.
   *
   * Les transactions l'emportent quand elles sont affichées — c'est le choix d'un compte
   * marchand, et le modèle « E-commerce » les active. À défaut, les conversions. Si aucune des
   * deux n'est à l'écran, le camembert disparaît : il n'aurait rien à représenter.
   */
  const mesureDuCamembert = isMetricActive('transactions')
    ? { cle: 'transactions', champ: 'transactions' }
    : (isMetricActive('conv') ? { cle: 'conv', champ: 'conversions' } : null);

  /** Le total de la mesure représentée, calculé sur les lignes affichées. */
  const totalDuCamembert = useMemo(() => (
    mesureDuCamembert
      ? (geography || []).reduce((somme, ligne) => somme + (ligne[mesureDuCamembert.champ] || 0), 0)
      : 0
  ), [geography]);

  const COLORS = ['#0088FE', '#82ca9d', '#FFBB28', '#FF8042', '#8884d8', '#00C49F', '#FF6B6B', '#4ECDC4'];


  // Fonction pour afficher une variation
  const renderVariation = makeRenderVariation({ fontSize: '11px', format: (v) => formatNumber(v, 1) });

  // Toggle functions for each level
  const toggleContinent = (label) => {
    setExpandedContinents(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const toggleCountry = (continentLabel, countryLabel) => {
    const key = `${continentLabel}-${countryLabel}`;
    setExpandedCountries(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleRegion = (continentLabel, countryLabel, regionLabel) => {
    const key = `${continentLabel}-${countryLabel}-${regionLabel}`;
    setExpandedRegions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Calculate totals
  const totals = useMemo(() => {
    if (!geography || !geography.length) return { visits: 0, visitors: 0, pageviews: 0, conversions: 0, revenue: 0, sumVisitLength: 0, bounceCount: 0, bounceRate: 0 };

    const cumul = geography.reduce((acc, continent) => ({
      visits: acc.visits + (continent.nb_visits || 0),
      visitors: acc.visitors + (continent.nb_uniq_visitors || 0),
      pageviews: acc.pageviews + (continent.nb_actions || 0),
      conversions: acc.conversions + ((continent.conversions ?? continent.nb_visits_converted) || 0),
      revenue: acc.revenue + (continent.revenue || 0),
      sumVisitLength: acc.sumVisitLength + (continent.sum_visit_length || 0),
      /*
       * Le **décompte** de rebonds s'additionne ; le taux, lui, se recalcule ensuite sur les
       * visites. Le pied de tableau n'affichait rien faute de le porter, et une moyenne des
       * taux par continent aurait donné à l'Océanie le poids de l'Europe.
       */
      bounceCount: acc.bounceCount + (continent.bounce_count || 0)
    }), {
      visits: 0, visitors: 0, pageviews: 0, conversions: 0, revenue: 0,
      sumVisitLength: 0, bounceCount: 0
    });

    return {
      ...cumul,
      bounceRate: cumul.visits > 0 ? (cumul.bounceCount / cumul.visits * 100) : 0
    };
  }, [geography]);

  // Calculate previous totals for comparison
  const previousTotals = useMemo(() => {
    if (!compareEnabled || !previousGeography || !previousGeography.length) return null;

    const baseTotals = previousGeography.reduce((acc, continent) => ({
      visits: acc.visits + (continent.nb_visits || 0),
      visitors: acc.visitors + (continent.nb_uniq_visitors || 0),
      pageviews: acc.pageviews + (continent.nb_actions || 0),
      conversions: acc.conversions + ((continent.conversions ?? continent.nb_visits_converted) || 0),
      revenue: acc.revenue + (continent.revenue || 0),
      sumVisitLength: acc.sumVisitLength + (continent.sum_visit_length || 0)
    }), { visits: 0, visitors: 0, pageviews: 0, conversions: 0, revenue: 0, sumVisitLength: 0 });

    return {
      ...baseTotals,
      conversionRate: baseTotals.visits > 0 ? (baseTotals.conversions / baseTotals.visits * 100) : 0,
      avgTime: baseTotals.visits > 0 ? (baseTotals.sumVisitLength / baseTotals.visits) : 0,
      pagesPerVisit: baseTotals.visits > 0 ? (baseTotals.pageviews / baseTotals.visits) : 0
    };
  }, [compareEnabled, previousGeography]);

  // Données pour le pie chart Visites
  const visitsChartData = useMemo(() => {
    if (!geography || !geography.length) return [];

    return geography
      .map(continent => ({
        name: continent.label,
        value: continent.nb_visits || 0,
        percentage: totals.visits > 0 ? ((continent.nb_visits / totals.visits) * 100).toFixed(1) : 0
      }))
      .filter(item => parseFloat(item.percentage) > 0);
  }, [geography, totals.visits]);

  // Données pour le pie chart Conversions
  const conversionsChartData = useMemo(() => {
    if (!geography || !geography.length || !mesureDuCamembert) return [];

    return geography
      .map(continent => ({
        name: continent.label,
        value: continent[mesureDuCamembert.champ] || 0,
        percentage: totalDuCamembert > 0 ? (((continent[mesureDuCamembert.champ] || 0) / totalDuCamembert) * 100).toFixed(1) : 0
      }))
      .filter(item => parseFloat(item.percentage) > 0);
  }, [geography, mesureDuCamembert, totalDuCamembert]);

  // Données pour la légende
  const legendData = useMemo(() => {
    if (!geography || !geography.length) return [];

    return geography.map((continent, index) => ({
      value: continent.label,
      color: COLORS[index % COLORS.length]
    }));
  }, [geography]);

  // Pagination
  const totalPages = Math.ceil(geography.length / ITEMS_PER_PAGE);
  const paginatedGeography = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return geography.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [geography, currentPage]);

  // Calculate derived metrics
  const calcBounceRate = (item) => {
    if (item.bounce_rate) return parseFloat(String(item.bounce_rate).replace('%', ''));
    if (item.nb_visits > 0 && item.bounce_count) return (item.bounce_count / item.nb_visits * 100);
    return 0;
  };

  const calcConversionRate = (item) => {
    if (item.nb_visits > 0 && (item.conversions ?? item.nb_visits_converted)) {
      return ((item.conversions ?? item.nb_visits_converted) / item.nb_visits * 100);
    }
    return 0;
  };

  /*
   * Les colonnes à rendre, **dans l'ordre du modèle**.
   *
   * `componentMetrics.order` porte la suite choisie ; `metrics` dit lesquelles sont actives.
   */
  const colonnes = useMemo(() => {
    const table = COLONNES(calcBounceRate, calcConversionRate);
    const ordre = componentMetrics.order?.length ? componentMetrics.order : componentMetrics.metrics;
    return (ordre || [])
      .filter((cle) => table[cle] && isMetricActive(cle))
      .map((cle) => ({ cle, ...table[cle] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentMetrics.order, componentMetrics.metrics, isMetricActive]);

  if (loading) {
    return (
      <>
        <div className="section-header">
          <h4>
            <Globe size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Géographie
          </h4>
        </div>
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          Chargement des données...
        </div>
      </>
    );
  }

  if (!geography || !geography.length) {
    return null;
  }

  return (
    <>
      <div className="section-header">
        <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Globe size={18} />
          Géographie
        </h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Géographie Matomo"
            />
          ) : (
            <button
              onClick={() => setIsVisible(!isVisible)}
              className="section-toggle-btn"
              title={isVisible ? "Masquer la section" : "Afficher la section"}
            >
              {isVisible ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {isVisible && (
        <>
          {/* Graphiques en secteurs côte à côte */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                  {/* Pie chart Visites (gauche) */}
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <h5 style={{ margin: '0 0 10px 0', color: '#374151', fontSize: '14px' }}>Sessions par continent</h5>
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={visitsChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ percentage }) => `${percentage}%`}
                          outerRadius={70}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {visitsChartData.map((_, index) => (
                            <Cell key={`visits-cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [formatNumber(value) + ' sessions', name]} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Légende commune (centre) */}
                  <div style={{
                    minWidth: '150px',
                    padding: '0 20px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    {legendData.map((item, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '8px',
                        fontSize: '13px'
                      }}>
                        <div style={{
                          width: '12px',
                          height: '12px',
                          backgroundColor: item.color,
                          marginRight: '8px',
                          borderRadius: '2px'
                        }}></div>
                        <span>{item.value}</span>
                      </div>
                    ))}
                  </div>

                  {/* Pie chart Conversions (droite) */}
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <h5 style={{ margin: '0 0 10px 0', color: '#374151', fontSize: '14px' }}>{mesureDuCamembert ? `${libelleColonne(mesureDuCamembert.cle)} par continent` : 'Conversions par continent'}</h5>
                    {totalDuCamembert > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie
                            data={conversionsChartData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ percentage }) => `${percentage}%`}
                            outerRadius={70}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {conversionsChartData.map((_, index) => (
                              <Cell key={`conversions-cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value, name) => [`${formatNumber(value)} ${libelleColonne(mesureDuCamembert.cle).toLowerCase()}`, name]} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                        {/* Le modèle « Contenu » n'active ni l'une ni l'autre : le dire
                            vaut mieux qu'un cadre vide et sans titre. */}
                        {mesureDuCamembert
                          ? `Aucune ${libelleColonne(mesureDuCamembert.cle).toLowerCase()}`
                          : 'Activez Conversions ou Transactions pour ce graphique'}
                      </div>
                    )}
                </div>
              </div>
            </div>

          {/* Tableau détaillé */}
          <div className="device-table-wrapper" style={{ marginTop: '0', paddingTop: '0' }}>
            <div className="device-summary-table" style={{ marginTop: '0', paddingTop: '0' }}>
              <table
                className="devices-table"
                style={{ width: '100%', tableLayout: 'auto', borderCollapse: 'collapse' }}
              >
                <thead>
                  <tr>
                    <th>Localisation</th>
                    {colonnes.map((colonne) => (
                      <th key={colonne.cle} className="numeric">{libelleColonne(colonne.cle)}</th>
                    ))}
                  </tr>
                </thead>

              <tbody>
                {paginatedGeography.map((continent, continentIndex) => {
                  const isContinentExpanded = expandedContinents[continent.label];
                  const hasCountries = continent.countries && continent.countries.length > 0;
                  // Trouver le continent correspondant dans les données de la période précédente
                  const prevContinent = compareEnabled && previousGeography
                    ? previousGeography.find(p => p.label === continent.label)
                    : null;

                  return (
                    <React.Fragment key={`continent-${continentIndex}`}>
                      {/* Continent row */}
                      <tr
                        className={hasCountries ? 'expandable-row' : ''}
                        onClick={() => hasCountries && toggleContinent(continent.label)}
                        style={{ cursor: hasCountries ? 'pointer' : 'default', fontWeight: '500' }}
                      >
                        <td className="device-name">
                          {hasCountries && (
                            <span style={{ marginRight: '4px', display: 'inline-flex', alignItems: 'center' }}>
                              {isContinentExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </span>
                          )}
                          {continent.label}
                        </td>
                        {colonnes.map((colonne) => (
                          <td key={colonne.cle} className="numeric">
                            {colonne.duree
                              ? formaterDuree(colonne.valeur(continent))
                              : colonne.format(colonne.valeur(continent), formatNumber)}
                            {prevContinent && renderVariation(
                              calculateVariation(colonne.valeur(continent), colonne.valeur(prevContinent)),
                              colonne.inverse
                            )}
                          </td>
                        ))}
                      </tr>

                      {/* Countries */}
                      {isContinentExpanded && continent.countries && continent.countries.map((country, countryIndex) => {
                        const countryKey = `${continent.label}-${country.label}`;
                        const isCountryExpanded = expandedCountries[countryKey];
                        const hasRegions = country.regions && country.regions.length > 0;

                        return (
                          <React.Fragment key={`country-${continentIndex}-${countryIndex}`}>
                            {/* Country row */}
                            <tr
                              className={hasRegions ? 'expandable-row detail-row' : 'detail-row'}
                              onClick={(e) => {
                                if (hasRegions) {
                                  e.stopPropagation();
                                  toggleCountry(continent.label, country.label);
                                }
                              }}
                              style={{
                                cursor: hasRegions ? 'pointer' : 'default',
                                backgroundColor: '#f9fafb',
                                fontSize: '0.95em'
                              }}
                            >
                              <td className="device-name" style={{ paddingLeft: '28px' }}>
                                {hasRegions && (
                                  <span style={{ marginRight: '4px', display: 'inline-flex', alignItems: 'center' }}>
                                    {isCountryExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                  </span>
                                )}
                                {!hasRegions && <span style={{ color: '#9ca3af' }}>└</span>}
                                {' '}{country.label}
                              </td>
                              {colonnes.map((colonne) => (
                                <td key={colonne.cle} className="numeric" style={{ color: '#4b5563' }}>
                                  {colonne.duree
                                    ? formaterDuree(colonne.valeur(country))
                                    : colonne.format(colonne.valeur(country), formatNumber)}
                                </td>
                              ))}
                            </tr>

                            {/* Regions */}
                            {isCountryExpanded && country.regions && country.regions.map((region, regionIndex) => {
                              const regionKey = `${continent.label}-${country.label}-${region.label}`;
                              const isRegionExpanded = expandedRegions[regionKey];
                              const hasCities = region.cities && region.cities.length > 0;

                              return (
                                <React.Fragment key={`region-${continentIndex}-${countryIndex}-${regionIndex}`}>
                                  {/* Region row */}
                                  <tr
                                    className={hasCities ? 'expandable-row' : ''}
                                    onClick={(e) => {
                                      if (hasCities) {
                                        e.stopPropagation();
                                        toggleRegion(continent.label, country.label, region.label);
                                      }
                                    }}
                                    style={{
                                      cursor: hasCities ? 'pointer' : 'default',
                                      backgroundColor: '#f3f4f6',
                                      fontSize: '0.9em'
                                    }}
                                  >
                                    <td className="device-name" style={{ paddingLeft: '48px' }}>
                                      {hasCities && (
                                        <span style={{ marginRight: '4px', display: 'inline-flex', alignItems: 'center' }}>
                                          {isRegionExpanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                                        </span>
                                      )}
                                      {!hasCities && <span style={{ color: '#9ca3af' }}>└</span>}
                                      {' '}{region.label}
                                    </td>
                                    {colonnes.map((colonne) => (
                                      <td key={colonne.cle} className="numeric" style={{ color: '#6b7280' }}>
                                        {colonne.duree
                                          ? formaterDuree(colonne.valeur(region))
                                          : colonne.format(colonne.valeur(region), formatNumber)}
                                      </td>
                                    ))}
                                  </tr>

                                  {/* Cities */}
                                  {isRegionExpanded && region.cities && region.cities.map((city, cityIndex) => (
                                    <tr
                                      key={`city-${continentIndex}-${countryIndex}-${regionIndex}-${cityIndex}`}
                                      style={{
                                        backgroundColor: '#f9fafb',
                                        fontSize: '0.85em'
                                      }}
                                    >
                                      <td className="device-name" style={{ paddingLeft: '68px' }}>
                                        <span style={{ color: '#9ca3af' }}>└</span> {city.label}
                                      </td>
                                      {colonnes.map((colonne) => (
                                        <td key={colonne.cle} className="numeric" style={{ color: '#9ca3af' }}>
                                          {colonne.duree
                                            ? formaterDuree(colonne.valeur(city))
                                            : colonne.format(colonne.valeur(city), formatNumber)}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </React.Fragment>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>

              <tfoot>
                <tr className="total-row">
                  <td className="device-name"><strong>TOTAL</strong></td>
                  {colonnes.map((colonne) => {
                    const valeur = colonne.total(totals);
                    return (
                      <td key={colonne.cle} className="numeric">
                        <strong>{colonne.cle === 'visiteurs' && visiteursDuSite !== null
                          ? formatNumber(visiteursDuSite)
                          : valeur === null
                            ? '-'
                            : colonne.duree
                              ? formaterDuree(valeur)
                              : colonne.format(valeur, formatNumber)}</strong>
                        {/*
                          `previousTotals` porte une **quatrième** famille de noms — `avgTime`,
                          `pagesPerVisit` — distincte des lignes comme des totaux courants.
                        */}
                        {valeur !== null
                          && !(colonne.cle === 'visiteurs' && visiteursDuSite !== null)
                          && previousTotals && colonne.totalPrecedent && renderVariation(
                          calculateVariation(valeur, colonne.totalPrecedent(previousTotals)),
                          colonne.inverse
                        )}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination-controls">
                <div className="pagination-info">
                  Affichage {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, geography.length)} sur {geography.length} continents
                </div>
                <div className="pagination-buttons">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="pagination-btn"
                  >
                    Précédent
                  </button>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="pagination-btn"
                  >
                    Suivant
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        </>
      )}

      {/* Modal de configuration des métriques */}
      <MetricsConfigModal
        isOpen={showMetricsConfig}
        onClose={() => setShowMetricsConfig(false)}
        componentId="matomo_geography"
        accountId={accountId}
        componentTitle="Géographie"
      />
    </>
  );
};

export default MatomoGeographyChart;
